import { getPasswordSchema } from "apps/visits/packages/shared/utils/yup";
import {
  getEmailSchema,
  getFirstNameSchema,
  getGenderSchema,
  getLastNameSchema,
  getOptionalCheckboxSchema,
  getPhoneSchema,
  getRequiredCheckboxSchema,
} from "apps/visits/packages/shared/validators";
import { getDateOfBirthSchema } from "apps/visits/packages/shared/validators/get-date-of-birth-schema";
import type { TFunction } from "i18next";
import * as yup from "yup";
import {
  getAdultDateOfBirthSchema,
  getAdultPeselSchema,
  getPeselWithValidBirthDateSchema,
} from "@/core/utils/adulthood";
import { applyCheckoutBlockedEmailDomainsToStringSchema } from "../../utils/checkout-blocked-email-domains";

// Explicit form contract. The schema is a `yup.lazy(...)` whose object shape is
// built from conditional field schemas, so `yup.InferType` collapses to `any`
// and erases every field type. We model the shape by hand instead; it is the
// single source of truth for the checkout form (RHF state, submit payload,
// CustomerData). Fields are stored as strings/booleans the way the inputs feed
// them (e.g. `dateOfBirth` is the masked string, not a Date); conditional fields
// are optional because the schema only requires them in certain branches.
export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  emailConfirmation: string;
  dialCode: string;
  phone: string;
  acceptDataProcessing: boolean;
  acceptTouNPp: boolean;
  acceptNewsletter: boolean;
  acceptMarketingPhoneContact: boolean;
  isGuestCheckout: boolean;
  orderType: "for_self" | "for_someone" | "skip";
  noPesel?: boolean;
  pesel?: string;
  dateOfBirth?: string;
  gender?: string;
  patientFirstName?: string;
  patientLastName?: string;
  patientDialCode?: string;
  patientPhone?: string;
  password?: string;
  passwordConfirmation?: string;
}

export const useValidationSchema = (
  t: TFunction,
  options: {
    bearerRequired?: boolean;
    showPasswordFields?: boolean;
    readonlyCustomerFields?: boolean;
    hidePhone?: boolean;
    checkoutBlockedEmailDomains?: string[];
    isGuestCheckout?: boolean;
    patientPhoneRequired?: boolean;
    allowMinorPatientForSomeone?: boolean;
  } = {},
) => {
  const {
    bearerRequired = true,
    showPasswordFields = false,
    readonlyCustomerFields = false,
    hidePhone = false,
    checkoutBlockedEmailDomains = [],
    isGuestCheckout = false,
    patientPhoneRequired = false,
    allowMinorPatientForSomeone = false,
  } = options;

  return yup.lazy((values: any) => {
    const hasCoreData =
      values.orderType === "for_someone"
        ? !!values.patientFirstName ||
          !!values.patientLastName ||
          !!values.pesel ||
          !!values.dateOfBirth ||
          !!values.gender ||
          !!values.patientPhone
        : !!values.pesel || !!values.dateOfBirth || !!values.gender;

    const coreRequired = bearerRequired || hasCoreData;
    // Read guest mode from options, not form values: a `<input type="hidden">`
    // round-trips booleans through the DOM as strings ("false" → truthy),
    // so trusting the form value here would always make password optional.
    const isGuest = isGuestCheckout;
    const skipEmail = readonlyCustomerFields || isGuest;

    // REV-100: the patient must be an adult. Friends & Family is the exception when the order is
    // for someone else — the account holder may buy a voucher for a child. "For self" means the
    // account holder either way, and holding an account already requires being 18.
    const adultPatientRequired = !(allowMinorPatientForSomeone && values.orderType === "for_someone");
    const peselSchema = adultPatientRequired ? getAdultPeselSchema(t) : getPeselWithValidBirthDateSchema(t);
    const dateOfBirthSchema = adultPatientRequired ? getAdultDateOfBirthSchema(t) : getDateOfBirthSchema(t);

    const baseSchema = {
      isGuestCheckout: yup.boolean(),
      firstName: readonlyCustomerFields ? yup.string().notRequired().nullable() : getFirstNameSchema(t),
      lastName: readonlyCustomerFields ? yup.string().notRequired().nullable() : getLastNameSchema(t),
      email: skipEmail
        ? yup.string().notRequired().nullable()
        : applyCheckoutBlockedEmailDomainsToStringSchema(getEmailSchema(t), t, checkoutBlockedEmailDomains),
      emailConfirmation: skipEmail
        ? yup.string().notRequired().nullable()
        : yup.string().oneOf([yup.ref("email")], t("COMMONS.ERRORS.EMAIL_MATCH")),
      dialCode: hidePhone ? yup.string().notRequired().nullable() : yup.string().required(t("COMMONS.ERRORS.REQUIRED")),
      phone: hidePhone ? yup.string().notRequired().nullable() : getPhoneSchema(t),
      acceptDataProcessing: getRequiredCheckboxSchema(t),
      acceptTouNPp: getRequiredCheckboxSchema(t),
      acceptNewsletter: getOptionalCheckboxSchema(t),
      acceptMarketingPhoneContact: getOptionalCheckboxSchema(t),
      noPesel: yup.boolean(),
      orderType: yup.string().oneOf(["for_self", "for_someone", "skip"]).default("for_self"),

      ...(showPasswordFields &&
        !isGuest && {
          password: getPasswordSchema(t),
          passwordConfirmation: yup
            .string()
            .required(t("COMMONS.ERRORS.REQUIRED"))
            .oneOf([yup.ref("password")], t("COMMONS.ERRORS.PASSWORD_MATCH")),
        }),

      patientFirstName:
        values.orderType === "for_someone"
          ? coreRequired
            ? getFirstNameSchema(t)
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),

      patientLastName:
        values.orderType === "for_someone"
          ? coreRequired
            ? getLastNameSchema(t)
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),

      pesel:
        values.orderType !== "skip" && !values.noPesel
          ? coreRequired
            ? peselSchema.required(t("COMMONS.ERRORS.REQUIRED"))
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),

      dateOfBirth:
        values.orderType !== "skip" && values.noPesel
          ? coreRequired
            ? dateOfBirthSchema.required(t("COMMONS.ERRORS.REQUIRED"))
            : yup.date().notRequired().nullable()
          : yup.date().notRequired().nullable(),

      gender:
        values.orderType !== "skip" && values.noPesel
          ? coreRequired
            ? getGenderSchema(t).required(t("COMMONS.ERRORS.REQUIRED"))
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),

      patientDialCode:
        values.orderType === "for_someone"
          ? values.patientPhone || patientPhoneRequired
            ? yup.string().required(t("COMMONS.ERRORS.REQUIRED"))
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),

      patientPhone:
        values.orderType === "for_someone"
          ? values.patientPhone || patientPhoneRequired
            ? getPhoneSchema(t)
            : yup.string().notRequired().nullable()
          : yup.string().notRequired().nullable(),
    };

    return yup.object(baseSchema);
  });
};
